import React, { createContext, useContext, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Toast from '../Toast';

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const showToast = useCallback((options) => {
    const id = Date.now() + Math.random();
    const toast = {
      id,
      ...options,
    };

    setToasts(prev => [...prev, toast]);

    // Auto-remove toast after duration (if specified)
    if (options.duration !== 0) {
      setTimeout(() => {
        removeToast(id);
      }, options.duration || 4000);
    }

    return id;
  }, [removeToast]);

  const showSuccess = useCallback((message, duration = 4000) => {
    return showToast({
      type: 'success',
      message,
      duration,
    });
  }, [showToast]);

  const showError = useCallback((message, duration = 4000) => {
    return showToast({
      type: 'error',
      message,
      duration,
    });
  }, [showToast]);

  const showWarning = useCallback((message, duration = 4000) => {
    return showToast({
      type: 'warning',
      message,
      duration,
    });
  }, [showToast]);

  const showInfo = useCallback((message, duration = 4000) => {
    return showToast({
      type: 'info',
      message,
      duration,
    });
  }, [showToast]);

  const showMessage = useCallback((options) => {
    return showToast({
      type: 'message',
      ...options,
    });
  }, [showToast]);

  const clearAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  const value = {
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showMessage,
    removeToast,
    clearAllToasts,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <div className="toast-container">
          {toasts.map((toast, index) => (
            <Toast
              key={toast.id}
              message={toast.message}
              type={toast.type}
              duration={toast.duration}
              onClose={() => removeToast(toast.id)}
              style={{ 
                top: `${80 + index * 60}px`,
                zIndex: 9999 - index 
              }}
            />
          ))}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
}; 